import { Fragment, type ReactNode } from 'react'
import { useParams } from 'react-router'

import type { ModuleRegistryRow } from '@ctcells/contract'
import { PATHS } from '@ctcells/core'
import { MODULE_REGISTRY } from '@ctcells/fixtures'
import { enumLabel, moduleTypeLabel, t } from '@ctcells/i18n'

import { EmptyState } from '../../atoms/EmptyState.tsx'
import { StatusBadge } from '../../atoms/StatusBadge.tsx'
import { type ValueRow, ValueRows } from '../../atoms/ValueRows.tsx'
import { SettingsRow } from '../../layout/SettingsList.tsx'
import type { ScreenAccess } from '../access.ts'
import { dateTimeText, numberText, staleValue } from '../format.ts'
import { Card, DetailPage } from '../resourceParts.tsx'
import { ModuleLinkChip } from './ModulesScreen.tsx'

import '../screens.css'
import '../resource.css'

/**
 * 모듈 상세 — 와이어프레임 `modules()` 상세 2624행.
 *
 * 🔴 **펌웨어 판은 2026-09-26 응답에 생겼다**(`ModuleSnapshot.fw_version` · `model`) — 연결 카드에
 *    두 줄로 선다. 서비스·컴파일러 판은 여전히 이 응답에 없다 — 그 둘은 되살리지 마라.
 * 🔴 **탭이 없다 — 개요 카드들이 바로 선다**(eric 2026-10-22 「위치 탭은 제거해줘」 · 「보정과 시스템 로그 탭은 제거해줘..」).
 *    개요 하나만 남아 탭 줄째 걷었다. 서랍 · 전체 화면(장비 관점 콘솔) 같다. 보정 · 로그 · 위치 화면을 여기 되살리지 마라.
 * 🔴 진단은 「제어」 카드의 「진단」 줄이다(`POST /modules/{id}/control/diagnostics` · `IFC-RO-48` · 2026-10-22 로컬 실측 200).
 *    ⚠ 종전 「실행 경로가 계약에 없다」는 2026-09-20 기준 서술이었다 — 지금은 경로가 있다(`ModuleControlRows.tsx`).
 */
export interface ModuleDetailScreenProps {
  modules?: readonly ModuleRegistryRow[]
  access?: ScreenAccess
  /**
   * 주소를 **못 받은 사유**. 🔴 **`—` 와 다른 사실이라 자리를 가른다.**
   *
   * `GET /modules/{id}/endpoint` 는 v0.9.3 까지 **admin 전용**이었다(v0.9.4 부터 모든 역할 · `ENDPOINT_READ_ROLES`).
   * 그때 operator 에게는 주소가 **늘** 비었는데, 종전에는 `host === ''` → `—` 로 떨어져
   * 「값이 없다」로 읽혔다. `—` 는 서버가 값을 안 준 것이고 이것은 **볼 권한이 없는 것**이다.
   *
   * ⚠ **열을 역할에 따라 빼지 않는다.** 같은 화면이 역할마다 모양이 다르면 혼란이다 —
   *   자리는 그대로 두고 **값 자리에 사유**를 세운다(규약: 숨기지 않고 비활성 + 사유).
   * ⚠ 「변경」 단추는 없다(eric 2026-10-22 「변경 접속 바꾸기 버튼 없애줘」) — 이 prop 은 **값 칸**만 맡는다.
   */
  addressNotice?: string | undefined
  /**
   * 서랍(목록 오른쪽 판)으로 열렸을 때의 닫기. 넘기면 「← 모듈」 줄 대신 **오른쪽 위 ×** 다
   * (eric 2026-09-22 「해당 row 를 클릭하면 상세 페이지 내용이 보이게」).
   * ⚠ **옵션이다** — 장비 관점 콘솔은 이 화면을 전체 화면으로 계속 쓴다.
   */
  onClose?: (() => void) | undefined
  /**
   * 「제어」 카드 자리(모듈 초기화 · 2026-09-28). **컨테이너가 채운다** — 화면은 훅을 모른다.
   * ⚠ 안 넘기면 카드가 없다. 통합 콘솔 서랍의 live 경로만 넘긴다(장비 관점 콘솔 · demo 는 그대로).
   */
  controlSlot?: ReactNode
  /**
   * 「연결」 카드 IP 주소 줄 바로 아래의 「브로커 주소」 줄(eric 2026-10-22 「브로커 주소..를 연결에 넣고」). **컨테이너가 채운다**
   * (`ModuleBrokerContainer` — 조회 · 권한 · 실패 사유를 그 줄 안에서 쥔다). 🔴 서랍에서만 선다 — 전체 화면(장비 관점 콘솔)은 넘기지 않는다
   */
  brokerRow?: ReactNode
}

export function ModuleDetailScreen({
  modules = MODULE_REGISTRY,
  addressNotice,
  onClose,
  controlSlot,
  brokerRow,
}: ModuleDetailScreenProps) {
  const { moduleId } = useParams()

  const module = modules.find((m) => m.module_id === moduleId)

  if (!module) {
    return (
      <DetailPage
        backLabel={t('screen.modules.title')}
        backTo={PATHS.modules}
        onClose={onClose}
        title={t('screen.moduleDetail.title')}
      >
        <EmptyState label={t('state.empty.generic')} />
      </DetailPage>
    )
  }

  /*
   * 🔴 **제목은 `module_type` 의 한글 이름이고, `module_id` 는 그 아래 줄에 «같이» 선다**
   *    (2026-09-21 실물 버그). 종전은 `moduleLabel(module.module_id as ModuleId)` 였는데
   *    실물 ID(`centrifuge-1`)가 사전에 없어 **제목에 `centrifuge-1` 이 그대로 떴다.**
   *    ID 꼴은 계약이 정한 게 아니라 **등록할 때 사람이 붙인 이름**이라 앞자리를 잘라
   *    종류를 읽어내는 것도 답이 아니다 — 종류는 서버가 `module_type` 으로 따로 준다.
   *
   * 🔴 **ID 를 같이 보이는 것이 규약이다.** 같은 종류가 2대다(2026-09-20 실측 `/modules` 8건
   *    = 살아 있는 4 + 폐기 4, 종류마다 2대). 이름만 보이면 **어느 대를 보고 있는지 알 수 없다.**
   *    자리는 `submeta` — 「제목=사람이 읽는 이름 · 그 아래=식별자」는 이 리포의 상세 화면이
   *    이미 쓰는 꼴이다(`UserDetailScreen` · `SampleDetailScreen`).
   *
   * ⚠ **모르는 종류(`ceiling_3axis`)는 ID «만»이다.** 「알 수 없는 모듈」을 지어내지 않고,
   *   `—`(= 값 없음)를 제목에 세우지도 않는다 — 그러면 「— · ceiling-1」이 된다.
   */
  const typeName = moduleTypeLabel(module.module_type)
  /*
   * 🔴 **사람이 붙인 이름(`display_name`)이 있으면 그것이 제목이다**(2026-09-26 응답 칸). 없으면
   *    종전대로 종류 라벨 → ID 다. 제목이 ID 가 아니면 ID 는 아랫줄에 «같이» 선다(위 규약).
   */
  const title = module.display_name ?? typeName ?? module.module_id

  /**
   * 서랍(모듈 목록 오른쪽 판)으로 열렸는가. 🔴 **`onClose` 가 그 축이다**(사용자 상세와 같다).
   * 전체 화면(장비 관점 콘솔)은 옆에 목록이 없어 값을 다 보여야 한다.
   */
  const drawer = onClose !== undefined

  /*
   * 🔴 **사유가 있으면 `—` 대신 사유다.** `—`(= `mask.none`)는 「값이 없다」이고
   *    사유는 「볼 권한이 없다」다 — 두 사실을 같은 글자로 말하지 않는다.
   *    `mono` 를 빼는 것도 일부러다: 주소가 아니라 문장이다.
   */
  const addressValue =
    addressNotice === undefined ? (
      <span className="mono">
        {module.host === '' ? t('mask.none') : `${module.host}:${String(module.port)}`}
      </span>
    ) : (
      <span className="muted">{addressNotice}</span>
    )

  return (
    /*
      🔴 **진단은 머리 단추가 아니라 「제어」 카드의 「진단」 줄이다**(eric 2026-10-22 「깔끔하게 추가해줘」 · `IFC-RO-48`).
         2026-09-20 에는 진단 경로가 오케스트레이터 계약에 0건이라 자리를 만들지 않았다 — 지금은 경로가 있다
         (`/modules/{id}/control/diagnostics` · 로컬 실측 200). 머리에 「자가 진단」 단추를 따로 되살리지 마라 — 두 벌이 된다.
      ⚠ **모듈 패드는 따로다.** 패드의 정비 탭은 모듈 로컬(`module_service` `/diagnostics`)을 쓰고 건드리지 않았다.
    */
    <DetailPage
      backLabel={t('screen.modules.title')}
      backTo={PATHS.modules}
      /*
       * 연결 칩 — 목록 「연결」 열과 한 벌(`ModuleLinkChip` · eric 2026-10-22). 머리 줄 오른쪽 끝(서랍이면 × 왼쪽 · 같은 날
       * 「여기서 연결 칩을 오른쪽으로 이동해줘」). 서랍 · 전체 화면 같은 자리.
       * 🔴 `badge` 가 아니라 `titleAside` 다 — 제목(`h2`) 밖에 세워 제목의 접근성 이름이 모듈 이름만이게 한다
       */
      titleAside={<ModuleLinkChip stale={module.stale} />}
      onClose={onClose}
      {...(title === module.module_id
        ? {}
        : { submeta: <span className="mono">{module.module_id}</span> })}
      title={title}
    >
      {/*
        🔴 **「연결 끊김 — 제어 불가」 알림을 여기 두지 않는다**(2026-09-18 eric 지시 —
           "모듈에서 아직도 끊김 제어불가가 떴다가 사라져.. 그거 안보이게 해").
           구독이 붙기 전 한 프레임 stale 로 판정돼 **떴다가 사라지는 깜빡임**이 났다.
        ⚠ **빠진 것은 표시뿐이다. 동작은 그대로다** — `gate()` 가 `access.controlDisabled` 를 보고
           상태변경 버튼을 계속 전부 비활성 + 사유(`reason.streamStale`)로 둔다(§6 규약 유지).
           스크린리더가 읽는 사유도 버튼에 그대로 붙어 있다.
        ⚠ `OfflineNotice` 자체를 지우지 마라 — 다른 화면 16곳이 쓴다. **모듈 3화면만** 뺀 것이다.
      */}
      {/*
        🔴 **「최근 진단」·「연결 이력」 카드를 되살리지 마라**(2026-09-20 제거).
           둘 다 **계약 경로가 0건**인데 화면이 fixture(`MODULE_*_HISTORY`)를 «직접» 읽고 있었다 —
           컨테이너를 거치지 않아서 **live 모드에서도 서버가 준 적 없는 값이 사실처럼** 떴다.
           전부 `—` 로 비는 것보다 나쁘다. 되살리려면 **계약부터** 세워라.
      */}
      {/*
        🔴 서랍의 카드는 `fstack` 으로 쌓는다(다른 상세 화면과 같은 카드 간격 · eric 2026-10-22 「카드 사이가 붙어 있다」).
           `modov` 는 머리(제목 · 부제)와 첫 카드 사이를 띄운다(장비 구성 요소 카드는 종전 그대로).
           ⚠ 전체 화면(장비 관점 콘솔)은 종전 그대로다 — 그 앱은 고치지 않는다
      */}
      <div className={drawer ? 'fstack modov' : undefined}>
        {/*
          🔴 **장비 구성 요소 카드(챔버 온도 · 셰이커 래치 · 인큐베이션 · 트레이 결착)는 없다**(eric 2026-10-22 「염색기에. 이거 없애줘.
             이거는 모니터링 화면에 있으면 되는 거라」). 그 값은 모니터링 장비 판이 쓴다 — 여기 되살리지 마라.
        */}
        <Card title={t('card.connection')}>
          {/*
            🔴 **응답에 있는 값만 둔다**(2026-09-20 eric 지시 — 「저 응답에 맞게 화면을 구성해」).
               「응답 확인」·「발견 시각」·「가동 시간」·「버전」 줄을 뺐다 — 어느 계약에도 없어
               실물에서 항상 `—` 였다. **되살리려면 계약부터 세워라.**
            ⚠ 주소는 남는다. 목록 응답에는 없지만 `GET /modules/{id}/endpoint` 에 실재한다.
          */}
          {/*
            🔴 **서랍에서는 목록에 끝까지 남는 열(상태 · 알람 · 안전)을 다시 적지 않는다**(eric 2026-09-23
               「이건 row 에 보이는 거니 상세에선 다 빼」). 연결 · 마지막 응답 · 제어 소유는 2026-09-28 부터
               **서랍에도 선다** — 서랍이 열리면 목록에서 빠지는 열이라서다(`listColumnPriority.ts`).
               **주소는 행에 없다** — `GET /modules/{id}/endpoint` 에서만 오고 「변경」 단추가
               붙어 있어 남는다.
            🔴 **전체 화면은 그대로다** — 장비 관점 콘솔에는 옆에 목록이 없다.
          */}
          {/*
            ⚠ 서랍에서는 줄마다 「이름 · 값」(`SettingsRow` — 사용자 상세 「계정 설정」과 같은 줄)이다. 이름 열 폭이
              카드마다 같아 값이 한 세로줄에 선다(eric 2026-10-22 「우리가 했던 다른 패턴으로 화면 좀 정리해줄래?」).
              🔴 주소 줄 「변경」 단추는 없다(eric 2026-10-22 「변경 접속 바꾸기 버튼 없애줘」 — 서랍 · 전체 화면 둘 다). 전체 화면은 종전 `ValueRows` 그대로다
          */}
          <OverviewRows
            after={brokerRow === undefined ? {} : { ip: brokerRow }}
            drawer={drawer}
            rows={[
              {
                key: 'ip',
                label: t('field.ipAddress'),
                value: addressValue,
              },
              /*
               * 🔴 모델 · 펌웨어 — 응답 칸(2026-09-26). **값이 있을 때만 줄이 선다**(eric 2026-09-28 서랍 캡처 —
               *    이름만 서고 값이 빈 줄). 실측 2026-09-28 `GET /modules` 살아 있는 넷(assembler-1 · centrifuge-1 ·
               *    robot6-1 · staining-1) 전부 `model: null` · `fw_version: null` — 레지스트리에 안 적혔다.
               *    ⚠ `—` 줄로 되돌리지 마라(「응답에 없는 값이면 그 자리를 만들지 않는다」)
               */
              ...(module.model === null || module.model === ''
                ? []
                : [
                    {
                      key: 'model',
                      label: t('row.model'),
                      value: <span className="mono">{module.model}</span>,
                    },
                  ]),
              ...(module.fw_version === null || module.fw_version === ''
                ? []
                : [
                    {
                      key: 'fw',
                      label: t('row.fwVersion'),
                      value: <span className="mono">{module.fw_version}</span>,
                    },
                  ]),
              ...(drawer
                ? []
                : [
                    {
                      key: 'state',
                      label: t('column.status'),
                      value: (
                        <StatusBadge
                          label={staleValue(
                            enumLabel('enum.moduleState', module.state),
                            module.stale,
                          )}
                          status={module.state}
                        />
                      ),
                    },
                  ]),
              /*
               * 🔴 **서랍에는 마지막 응답 · 제어 소유 줄이 없다**(eric 2026-10-22 「제어 소유 시스템 이거. 모듈 상세에서 빼줘... 마지막 응답도 빼고...」).
               *    마지막 응답은 목록에 끝까지 남는 열이 됐다(`listColumnPriority.ts` 에서 뺐다) — 서랍이 열려도 목록이 말한다.
               *    「연결」 줄도 없다 — 머리 오른쪽 칩이다. 상태 · 알람 · 안전도 서랍에서는 빠진다(종전 그대로).
               * ⚠ 전체 화면(장비 관점 콘솔)은 옆에 목록이 없어 다 보인다 — 그 앱은 고치지 않는다.
               */
              ...(drawer
                ? []
                : [
                    // 🔴 상대 시간이 아니라 시각이다. 「방금」은 화면이 타이머를 돌려 만드는 값이다. 값이 없으면 줄이 없다
                    ...(module.last_seen === null || module.last_seen === ''
                      ? []
                      : [
                          {
                            key: 'lastSeen',
                            label: t('row.lastSeen'),
                            value: <span className="mono">{dateTimeText(module.last_seen)}</span>,
                          },
                        ]),
                    {
                      key: 'owner',
                      label: t('panel.controlOwner'),
                      value: enumLabel('enum.controlOwner', module.control_owner),
                    },
                    {
                      key: 'alarm',
                      label: t('column.alarm'),
                      value:
                        module.active_alarm_count === null || module.active_alarm_count === 0
                          ? t('mask.none')
                          : numberText(module.active_alarm_count),
                    },
                    {
                      // 🔴 미발행을 「정상」이라고 하지 않는다 — 모르면 `—`
                      key: 'safety',
                      label: t('column.safety'),
                      value:
                        module.e_stop === true ? (
                          <b>{t('module.eStopEngaged')}</b>
                        ) : module.interlock_state === null ? (
                          t('mask.none')
                        ) : module.interlock_state === 'closed' ? (
                          enumLabel('enum.interlock', 'closed')
                        ) : (
                          <b>{enumLabel('enum.interlock', module.interlock_state)}</b>
                        ),
                    },
                  ]),
            ]}
          />
        </Card>

        {/* 「제어」 · 「브로커 접속」 · 「모듈 관리」 카드 — 컨테이너가 채운 것만 선다 */}
        {controlSlot}
      </div>
    </DetailPage>
  )
}

/**
 * 개요 탭 연결 카드의 줄 — 서랍이면 `SettingsRow`(이름 · 값 — 사용자 상세 「계정 설정」과 같은 줄), 전체 화면이면 종전 `ValueRows`.
 * 🔴 줄 구성(어느 줄이 서는가)은 부르는 쪽 한 벌이다 — 두 모양이 같은 `rows` 를 읽는다.
 */
function OverviewRows({
  rows,
  drawer,
  after = {},
}: {
  rows: readonly ValueRow[]
  drawer: boolean
  /** 줄 `key` → 그 줄 바로 아래 끼울 줄(서랍에서만 · 「브로커 주소」 줄처럼 컨테이너가 채운 것) */
  after?: Readonly<Record<string, ReactNode>>
}) {
  if (!drawer) return <ValueRows rows={rows} />
  return (
    <>
      {rows.map((row) => (
        <Fragment key={row.key}>
          <SettingsRow label={row.label} value={row.value} />
          {after[row.key]}
        </Fragment>
      ))}
    </>
  )
}
